import type { ColDef } from "ag-grid-community";
import dayjs from "dayjs";
import React from "react";

import { Badge } from "@/components/ui/badge";
import { Heading } from "@/components/ui/heading";
import { Link } from "@/components/ui/link";
import { SearchField, SearchInput } from "@/components/ui/search-field";
import { Text } from "@/components/ui/text";
import type { MasterInvoiceDetail } from "@/features/admin/types";
import {
  accessionColumn,
  alternateIdColumn,
  dateColumn,
  descriptionColumn,
  donorIdColumn,
  orgColumn,
  priceColumn,
  unosIdColumn,
} from "@/features/admin/utils/columns";
import { formatCurrencyValue } from "@/features/admin/utils/currency";
import { useAdminGridOptions } from "@/features/admin/utils/gridState";
import { AdminGrid, denseAdminGridTheme } from "../AdminGrid";

type LineItem = {
  instructionId: number;
  accessionId: number;
  description: string;
  price: { toNumber(): number };
  caseId: string | null;
  unosId: string | null;
  donorIds: string[];
  invoice?: { org: { name: string } } | null;
  org?: { name: string } | null;
  accession: {
    date: Date;
    splitBillings: { org: { name: string } }[];
  };
  splitOrg?: { name: string } | null;
};

type DetailData = MasterInvoiceDetail;
type PreviewData = { lineItems: LineItem[]; total: number };

type Props =
  | { data: DetailData | undefined; isPreview?: false }
  | { data: PreviewData | undefined; isPreview: true };

function isDetailData(
  data: DetailData | PreviewData | undefined,
  isPreview: boolean | undefined,
): data is DetailData {
  return !isPreview && data !== undefined && "document" in data;
}

export function MasterInvoicePreview({ data, isPreview }: Props) {
  const [search, setSearch] = React.useState("");

  const lineItems: LineItem[] = isDetailData(data, isPreview)
    ? data.lineItems
    : ((data as PreviewData | undefined)?.lineItems ?? []);

  const document = isDetailData(data, isPreview) ? data.document : undefined;
  const total = isDetailData(data, isPreview)
    ? data.total
    : ((data as PreviewData | undefined)?.total ?? 0);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden rounded-lg border bg-background shadow-xs">
      <div className="flex flex-col gap-6 px-6 py-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2">
            <Text
              muted
              size="xs"
              className="font-medium uppercase tracking-wide"
            >
              {isPreview ? "Status" : "Generated"}
            </Text>
            {isPreview && (
              <Badge variant="soft" color="amber" size="sm">
                Pending - Preview
              </Badge>
            )}
          </div>
          <Heading level={2} displayLevel={3} className="font-semibold">
            {isPreview
              ? "Master invoice preview"
              : document
                ? dayjs(document.billingRun.createdAt).format("MMMM D, YYYY")
                : "-"}
          </Heading>
          {isPreview && (
            <Text muted size="sm">
              Combined preview of all pending line items.
            </Text>
          )}
        </div>
        <dl className="grid grid-cols-[auto_auto] items-baseline gap-x-6 gap-y-2 sm:justify-end">
          {document && (
            <>
              <Text elementType="dt" muted size="sm">
                Format
              </Text>
              <Text elementType="dd" size="sm" className="text-right">
                {document.format === "PDF" ? "PDF" : "Excel"}
              </Text>
              <Text elementType="dt" muted size="sm">
                Recipient
              </Text>
              <Text elementType="dd" size="sm" className="text-right break-all">
                {document.recipientEmail}
              </Text>
            </>
          )}
          <Text elementType="dt" muted size="sm">
            Total
          </Text>
          <Text
            elementType="dd"
            size="md"
            bold
            className="text-right tabular-nums"
          >
            {formatCurrencyValue(total)}
          </Text>
        </dl>
      </div>
      <div className="flex items-center justify-between gap-3 border-t bg-muted/30 px-6 py-3">
        <Text size="sm" bold>
          Line items
        </Text>
        <SearchField
          value={search}
          onChange={setSearch}
          aria-label="Search line items"
          className="w-64"
        >
          <SearchInput placeholder="Search line items..." />
        </SearchField>
      </div>
      <div className="flex flex-1 min-h-0 flex-col border-t">
        <MasterLineItemsTable lineItems={lineItems} quickFilterText={search} />
      </div>
    </div>
  );
}

function MasterLineItemsTable({
  lineItems,
  quickFilterText,
}: {
  lineItems: LineItem[];
  quickFilterText: string;
}) {
  const columnDefs = React.useMemo<ColDef<LineItem>[]>(
    () => [
      {
        ...dateColumn,
        colId: "date",
        headerName: "Date",
        maxWidth: 140,
        valueGetter: ({ data }) => data?.accession.date ?? null,
      },
      {
        ...orgColumn,
        colId: "billedOrg",
        headerName: "Billed Org",
        valueGetter: ({ data }) =>
          data?.invoice?.org.name ?? data?.org?.name ?? "-",
      },
      {
        ...accessionColumn,
        colId: "accessionId",
        headerName: "Accession ID",
        minWidth: 130,
        valueGetter: ({ data }) => data?.accessionId ?? null,
        cellRenderer: ({ data }: { data?: LineItem }) =>
          data ? (
            <Link
              href={{
                to: "/accessions/$id",
                params: { id: data.accessionId },
              }}
            >
              {data.accessionId}
            </Link>
          ) : null,
      },
      {
        ...donorIdColumn,
        colId: "donorId",
        headerName: "Donor ID",
        valueGetter: ({ data }) => data?.caseId ?? "",
      },
      {
        ...unosIdColumn,
        colId: "unosId",
        headerName: "UNOS ID",
        valueGetter: ({ data }) => data?.unosId ?? "",
      },
      {
        ...alternateIdColumn,
        colId: "alternateId",
        headerName: "Alternate ID",
        valueGetter: ({ data }) => data?.donorIds.join(", ") ?? "",
      },
      {
        ...descriptionColumn,
        field: "description",
        headerName: "Description",
      },
      {
        ...orgColumn,
        colId: "splitWith",
        headerName: "Split With",
        flex: undefined,
        valueGetter: ({ data }) => {
          if (!data) return "";
          if (data.accession.splitBillings.length > 0) {
            return data.accession.splitBillings
              .map((billing) => billing.org.name)
              .join(", ");
          }
          return data.splitOrg?.name ?? "-";
        },
      },
      {
        ...priceColumn,
        colId: "price",
        headerName: "Price",
        maxWidth: 140,
        valueGetter: ({ data }) => data?.price.toNumber() ?? 0,
      },
    ],
    [],
  );

  const { options } = useAdminGridOptions<LineItem>(lineItems, columnDefs, {
    getRowId: ({ data }) => String(data.instructionId),
    quickFilterText,
    defaultColDef: {
      flex: 1,
      resizable: true,
      sortable: false,
      filterParams: { maxNumConditions: 1 },
    },
    suppressDragLeaveHidesColumns: true,
  });

  return <AdminGrid<LineItem> {...options} theme={denseAdminGridTheme} />;
}
